import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  ConfirmModal,
  Heading3,
  Heading4,
  TextL,
  TextM,
  TextS,
} from 'app/components';
import { Divider, Theme, Tooltip, useTheme } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DisbursementDatesSelector,
  QRCodeContainer,
  ReviewSimulationBox,
  ReviewSimulationPreviewItem,
  ReviewSimulationPreviewItems,
  ReviewStepBorrowerBox,
  ReviewStepBox,
  ReviewStepContent,
  SignTypeContainer,
  TedModalContent,
} from './styles';
import { ApplicationRequestContext } from '../ApplicationRequest';
import { StepContentButtonsArea } from '../styles';
import { ArrowBack, InfoOutlined } from '@mui/icons-material';
import {
  capitalize,
  formatCurrency,
  normalizeAddDigits,
  normalizeCpfCnpj,
} from 'app/utils/normalizer';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import { useApplicationsService } from 'modules/products/services/hooks';
import { CopyIcon, HandCoinBiggerIcon } from 'app/components/Icons';
import { TBorrowerType } from '../BorrowerStep/BorrowerStep';
import {
  ACCOUNT_TYPE_OBJECT,
  BANK_LIST_OBJECT,
  formatCNPJ,
  formatCPF,
  formatPhone,
} from 'app/helpers';
import { useNavigate } from 'react-router-dom';
import { SubscriberName } from '../SubscribersStep/SubscribersStep';
import { Table, CustomRadioInput } from 'app/components';
import { useSnackbar } from 'app/hooks/useSnackbar';
import {
  TableBodyCellContainer,
  TableHeadCellContainer,
} from 'app/styles/components/Table';
import { PixType } from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { useAppContext } from 'app/context/AppContextProvider';
import { differenceInDays, format, isSameDay, startOfDay } from 'date-fns';
import AttentionCircularIcon from 'app/components/Icons/AttentionCircularIcon';
import { CustomSelect } from 'app/components/CustomSelect';
import useCreditSimulator from 'app/hooks/useCreditSimulator';
import { SignatureProvider } from '../../interfaces';
import {
  SignatureAuthenticationMode,
  SignatureUnicoAuthenticationMode,
} from 'modules/products/services/api/applicationsHttpService';
import {
  EBeneficiaryType,
  TypeProductEmission,
} from 'modules/products/context/ProductsProvider/products.interfaces';
import { getBilletNumber } from 'modules/products/helpers/billets';
import { QRCodeDetails } from 'app/components/QRCode';
import CreditSimulatorSchedule from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2/CreditSimulatorSchedule';
import Accordion from 'app/components/Accordion/Accordion';
import { ConsigneeOrganType } from '../ConsignedStep/interfaces';
import { ESplitPaymentType } from 'modules/products/interfaces/splitPayment';
import FgtsReviewTable from './FgtsTable/FgtsTable';

enum PaymentMethod {
  PIX = 'PIX',
  TED = 'TED',
}

enum TedErrorType {
  BORROWER,
  HOUR,
}

const ReviewStepSimulationDivider = ({ theme }: { theme: Theme }) => (
  <Divider
    sx={{
      backgroundColor: theme.palette?.brand.primary.base,
      marginTop: '12px',
      opacity: 0.16,
    }}
  />
);

const tableHeadContent = [
  { id: '1', content: null },
  {
    id: '2',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">Valor no vencimento</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '3',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">Amortização</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '4',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">Juros</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '5',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">IOF</TextM>
      </TableHeadCellContainer>
    ),
  },
  {
    id: '5',
    content: (
      <TableHeadCellContainer>
        <TextM weight="Bold">Vencimento</TextM>
      </TableHeadCellContainer>
    ),
  },
];

const signatureOptions = [
  {
    value: SignatureAuthenticationMode.HANDWRITTEN,
    label: 'Assinatura manuscrita',
  },
  {
    value: SignatureAuthenticationMode.SELFIE,
    label: 'Selfie com documento',
  },
  {
    value: SignatureAuthenticationMode.ADDRESS_PROOF,
    label: 'Comprovante de endereço',
  },
  {
    value: SignatureAuthenticationMode.OFFICIAL_DOCUMENT,
    label: 'Documento oficial',
  },
  {
    value: SignatureAuthenticationMode.LIVENESS,
    label: 'Selfie dinâmica',
  },
  {
    value: SignatureAuthenticationMode.PIX,
    label: 'Pix',
  },
  {
    value: SignatureAuthenticationMode.FACIAL_BIOMETRICS,
    label: 'Biometria Facial',
  },
  {
    value: SignatureAuthenticationMode.ICP_BRASIL,
    label: 'Certificado Digital',
  },
  {
    value: SignatureAuthenticationMode.EMAIL,
    label: 'E-mail',
  },
  {
    value: SignatureAuthenticationMode.SMS,
    label: 'SMS',
  },
  {
    value: SignatureAuthenticationMode.WHATSAPP,
    label: 'WhatsApp',
  },
];

const signatureUnicoOptions = [
  {
    value: SignatureUnicoAuthenticationMode.DOC_SIGN_SERPRO,
    label: 'Serpro',
  },
  {
    value: SignatureUnicoAuthenticationMode.DOC_SIGN,
    label: 'Padrão',
  },
];

const ReviewStep = () => {
  /* Hooks */
  const {
    simulation,
    simulationRequest,
    simulationV2,
    simulationV2Request,
    product,
    borrowerData,
    subscribers,
    customVariables,
    funding,
    beneficiaryAccount,
    basePremiumAmount,
    resimulation,
    custodian,
    billetValidationResponse,
    qrcodeEmvValidationResponse,
    splitPayments,
    simulateWithDifferentDisbursementDate,
    onBack,
    onBackTwoSteps,
    hasVariablesStep,
    consignedProduct: consigned,
    consignedConfig,
    customCcbTemplateId,
    fgtsPeriods,
  } = useContext(ApplicationRequestContext);
  const { feature_flags } = useAppContext();
  const { loadings, createApplication } = useApplicationsService();
  const { showSnackbarCopy } = useSnackbar();
  const { simulationLoading } = useCreditSimulator();
  const theme = useTheme();
  const navigate = useNavigate();

  /* States */
  const [isConfirmModalOpen, toggleConfirmModalOpen] = useState(false);
  const [selectedSignatureType, setSelectedSignatureType] = useState('LINK');
  const [signatureMethod, updateSignatureMethod] =
    useState<SignatureAuthenticationMode>(
      SignatureAuthenticationMode.FACIAL_BIOMETRICS,
    );
  const [signatureMethodUnico, setSignatureMethodUnico] =
    useState<SignatureUnicoAuthenticationMode>(
      SignatureUnicoAuthenticationMode.DOC_SIGN_SERPRO,
    );
  const [selectedSignatureProvider, setSelectedSignatureProvider] = useState<
    string | undefined
  >(undefined);
  const [tedErrorType, setTedErrorType] = useState<TedErrorType | undefined>();
  const [paymentMethod, setSelectedPaymentMethod] = useState<
    undefined | PaymentMethod
  >();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isQRCodeAccordionOpen, setIsQRCodeAccordionOpen] = useState(false);
  const [isBilletsAccordionOpen, setIsBilletsAccordionOpen] = useState(false);

  /* Variables */
  const borrowerType: TBorrowerType = product?.borrower_type as TBorrowerType;
  const tableHeader = [
    'Função',
    'Pessoa',
    borrowerType === 'PERSON' ? 'CPF' : 'CNPJ',
    'Telefone',
    'E-mail',
  ];

  const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
  const isNewSimulator = useMemo(() => {
    return feature_flags?.NEW_SIMULATOR_ENABLED
      ? feature_flags?.NEW_SIMULATOR_ENABLED?.isActive
      : false;
  }, [feature_flags]);

  const isPaymentMethodVisible = useMemo(() => {
    return feature_flags?.PORTAL_TED_ENABLED
      ? feature_flags?.PORTAL_TED_ENABLED?.isActive
      : false;
  }, [feature_flags]);

  const availableSignatureProviders = useMemo(() => {
    const defaultProviders = [
      { value: SignatureProvider.CLICKSIGN, label: 'Clicksign' },
      { value: SignatureProvider.ZAPSIGN, label: 'ZapSign' },
    ];

    if (
      !product?.allowed_signature_providers ||
      product.allowed_signature_providers.length === 0
    ) {
      return defaultProviders;
    }

    const allProviders = [
      { value: SignatureProvider.CLICKSIGN, label: 'Clicksign' },
      { value: SignatureProvider.ZAPSIGN, label: 'ZapSign' },
      { value: SignatureProvider.CELCOIN, label: 'Celcoin' },
      { value: SignatureProvider.UNICO, label: 'Unico' },
    ];

    return allProviders.filter((provider) =>
      product.allowed_signature_providers?.includes(provider.value),
    );
  }, [product?.allowed_signature_providers]);

  const splitBeneficiaries = useMemo(
    () =>
      splitPayments.filter(
        (payment) => payment.type === ESplitPaymentType.BENEFICIARY,
      ),
    [splitPayments],
  );

  const shouldSkipVariablesStep = useMemo(
    () => !hasVariablesStep,
    [hasVariablesStep],
  );

  useEffect(() => {
    if (availableSignatureProviders.length > 0 && !selectedSignatureProvider) {
      setSelectedSignatureProvider(availableSignatureProviders[0].value);
    }
  }, [availableSignatureProviders, selectedSignatureProvider]);

  const onCreateApplication = useCallback(() => {
    const signatureMethodToUse =
      selectedSignatureProvider === SignatureProvider.UNICO
        ? signatureMethodUnico
        : signatureMethod;
    const hasSimulation = isNewSimulator
      ? !!simulationV2Request
      : !!simulationRequest;
    if (hasSimulation && product && selectedSignatureProvider) {
      createApplication(
        product.id,
        borrowerData?.id || '',
        funding as string,
        customVariables,
        subscribers,
        selectedSignatureType,
        beneficiaryAccount,
        custodian,
        billetValidationResponse,
        qrcodeEmvValidationResponse,
        paymentMethod,
        simulationRequest,
        simulationV2Request,
        selectedSignatureProvider as SignatureProvider | undefined,
        signatureMethodToUse,
        consigned?.balance_check_id,
        splitPayments,
        simulationV2Request?.financialIndicatorCobransaas,
        simulationV2,
        customCcbTemplateId,
      ).then((application) => {
        if (application !== null) {
          navigate(`/products/application/${application.id}/signatures`);
        }
        toggleConfirmModalOpen(false);
      });
    }
  }, [
    simulationRequest,
    product,
    selectedSignatureType,
    beneficiaryAccount,
    paymentMethod,
    selectedSignatureProvider,
    signatureMethod,
    consigned,
    simulationV2Request,
    subscribers,
    signatureMethodUnico,
  ]);

  const onChangePaymentMethod = useCallback(
    (method: PaymentMethod) => {
      if (method === PaymentMethod.TED) {
        if (
          !borrowerData?.external_bank_account ||
          !borrowerData?.external_bank_account.bank_account
        ) {
          setTedErrorType(TedErrorType.BORROWER);
          return;
        }

        if (
          !simulationRequest?.disbursementDate ||
          (isSameDay(
            new Date(),
            new Date(simulationRequest?.disbursementDate),
          ) &&
            new Date().getHours() >= 16)
        ) {
          setTedErrorType(TedErrorType.HOUR);
          return;
        }
      }
      setSelectedPaymentMethod(method);
    },
    [borrowerData, simulationRequest],
  );

  const handleSelectSignatureType = (value: string) => {
    setSelectedSignatureType(value);
  };

  const isPerson = () => {
    return borrowerType === 'PERSON';
  };

  const baseIofPerInstallment = useMemo(() => {
    const sum_running_iof =
      simulation?.schedule?.reduce((sum, j) => sum + j.iof, 0) || 0;

    if (!simulation?.iofAmount || !simulation.numPeriods) {
      return 0;
    }

    return (simulation?.iofAmount - sum_running_iof) / simulation.numPeriods;
  }, [simulation]);

  const handleCopySchedule = useCallback(() => {
    const header =
      'Parcela\tValor no vencimento\tAmortização\tJuros\tIOF\tVencimento\r\n';
    const records = simulation?.schedule
      ?.map((schedule, inx) => {
        const { payment, principal, interest, payment_date, iof } = schedule;
        const finalIof = baseIofPerInstallment + iof;
        return `${
          inx + 1
        }\t${payment.toLocaleString()}\t${principal.toLocaleString()}\t${interest.toLocaleString()}\t${finalIof}\t${payment_date}`;
      })
      .join('\r\n');

    navigator.clipboard.writeText(`${header}${records}`);
    showSnackbarCopy();
  }, [simulation, baseIofPerInstallment]);

  const getFormatedPix = (pixKey?: string, pixType?: PixType): string => {
    if (!pixKey || !pixType || pixKey.length === 0 || pixType.length === 0)
      return '-';
    if (pixType === PixType.TAXPAYER_ID) return normalizeCpfCnpj(pixKey);
    if (pixType === PixType.PHONE_NUMBER) return formatPhone(pixKey);
    return pixKey;
  };

  // Calculo do saldo de liquidação
  const cessionValue = useMemo(() => {
    if (simulationV2) {
      if (!simulationV2) return 0;
      const tacAmount = product?.tac_not_included_cession_amount
        ? 0
        : simulationV2.tac_amount || 0;
      return (
        (simulationV2.requested_amount ?? 0) +
        tacAmount +
        (simulationV2.finance_fee ?? 0) +
        (simulationV2.iof_amount ?? 0) +
        (basePremiumAmount ?? 0) -
        (simulation?.discount_amount ?? 0)
      );
    }

    if (!simulation) return 0;
    const tacAmount = product?.tac_not_included_cession_amount
      ? 0
      : simulation.tacAmount || 0;
    return (
      (simulation.requested_amount ?? 0) +
      tacAmount +
      (simulation.finance_fee ?? 0) +
      (simulation.iofAmount ?? 0) +
      (basePremiumAmount ?? 0) -
      (simulation?.discount_amount ?? 0)
    );
  }, [simulation, basePremiumAmount, product, simulationV2]);

  const finalSimulation = resimulation === null ? simulation : resimulation;
  const successBillets =
    billetValidationResponse?.billets_info?.filter(
      (billet) => billet.succeeded,
    ) ?? [];

  return (
    <BoxSection>
      <ReviewStepContent>
        <Heading3 style={{ marginBottom: '16px' }}>Ficou tudo certo?</Heading3>
        {isFgts && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              FGTS
            </Heading4>
            <FgtsReviewTable
              data={fgtsPeriods || []}
              simulationResponse={simulationV2}
            />
          </ReviewStepBox>
        )}
        {consigned && product?.organ_type === ConsigneeOrganType.SIAPE && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              Consignado Siape
            </Heading4>
            <ReviewSimulationBox>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Órgão
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {consignedConfig?.employer?.name || 'Indisponível'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Matrícula do tomador
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {consignedConfig?.employee?.code || '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Classificação
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {consignedConfig?.meta_data?.classification_description ||
                        'Não informado'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Código do órgão
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {consignedConfig?.employer?.code || 'Indisponível'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Tipo de vínculo
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {consignedConfig?.meta_data
                        ?.functional_status_description || '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Nome do produto
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Empréstimo Siape
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
            </ReviewSimulationBox>
          </ReviewStepBox>
        )}
        {beneficiaryAccount && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              Conta Beneficiária
            </Heading4>
            <ReviewSimulationBox>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Descrição
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.description}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Nome
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.holder.name}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.holder.taxpayer_id.length > 11
                        ? 'CNPJ'
                        : 'CPF'}
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {normalizeCpfCnpj(beneficiaryAccount.holder.taxpayer_id)}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Pix
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.pix
                        ? getFormatedPix(
                            beneficiaryAccount.pix.key,
                            beneficiaryAccount.pix.key_type as PixType,
                          )
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Banco
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.external_bank_account?.ispb_code
                        ? BANK_LIST_OBJECT[
                            beneficiaryAccount.external_bank_account.ispb_code
                          ]
                          ? `${
                              BANK_LIST_OBJECT[
                                beneficiaryAccount.external_bank_account
                                  .ispb_code
                              ]
                            } (${
                              beneficiaryAccount.external_bank_account.ispb_code
                            })`
                          : '-'
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Conta
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.external_bank_account?.bank_account
                        ? `${beneficiaryAccount.external_bank_account.bank_account}-${beneficiaryAccount.external_bank_account.bank_account_digit}`
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Agência
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.external_bank_account?.bank_branch
                        ? beneficiaryAccount.external_bank_account.bank_branch
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Tipo
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {beneficiaryAccount.external_bank_account
                        ?.bank_account_type
                        ? ACCOUNT_TYPE_OBJECT[
                            beneficiaryAccount.external_bank_account
                              .bank_account_type
                          ] ?? '-'
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
            </ReviewSimulationBox>
          </ReviewStepBox>
        )}
        {borrowerData?.external_bank_account?.bank_account && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              Conta do Tomador
            </Heading4>
            <ReviewSimulationBox>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Nome
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {isPerson()
                        ? borrowerData?.full_name
                        : borrowerData?.legal_name}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerType === 'PERSON' ? 'CPF' : 'CNPJ'}
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerType === 'PERSON'
                        ? formatCPF(borrowerData?.taxpayer_id || '-')
                        : formatCNPJ(borrowerData?.taxpayer_id || '-')}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Banco
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerData?.external_bank_account?.ispb_code
                        ? BANK_LIST_OBJECT[
                            borrowerData.external_bank_account.ispb_code
                          ]
                          ? `${
                              BANK_LIST_OBJECT[
                                borrowerData.external_bank_account.ispb_code
                              ]
                            } (${borrowerData.external_bank_account.ispb_code})`
                          : borrowerData.external_bank_account.ispb_code
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Tipo
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerData?.external_bank_account?.bank_account_type
                        ? ACCOUNT_TYPE_OBJECT[
                            borrowerData.external_bank_account.bank_account_type
                          ] ?? '-'
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Chave Pix
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerData?.pix
                        ? getFormatedPix(
                            borrowerData.pix.key,
                            borrowerData.pix.key_type as PixType,
                          )
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Agência
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerData?.external_bank_account?.bank_branch || '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Conta
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {borrowerData?.external_bank_account?.bank_account
                        ? `${borrowerData.external_bank_account.bank_account}-${borrowerData.external_bank_account.bank_account_digit}`
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
            </ReviewSimulationBox>
          </ReviewStepBox>
        )}
        {splitBeneficiaries.length > 0 &&
          splitBeneficiaries.map((beneficiary) => (
            <ReviewStepBox
              key={`${beneficiary?.taxpayer_id}-${
                beneficiary?.amountString || ''
              }`}
              marginTop="0px"
              marginBottom="16px"
            >
              <Heading4
                style={{
                  color: theme.palette.brand.secondary.base,
                }}
              >
                Conta do Beneficiário
              </Heading4>
              <ReviewSimulationBox>
                <ReviewSimulationPreviewItems>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        Nome
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.name || '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        CPF / CNPJ
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.taxpayer_id
                          ? normalizeCpfCnpj(beneficiary.taxpayer_id)
                          : '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        Chave Pix
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.pix?.key
                          ? getFormatedPix(
                              beneficiary.pix.key,
                              beneficiary.pix.key_type as PixType,
                            )
                          : '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                </ReviewSimulationPreviewItems>
                <ReviewSimulationPreviewItems>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        Banco
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.external_bank_account?.ispb_code
                          ? BANK_LIST_OBJECT[
                              beneficiary.external_bank_account.ispb_code
                            ]
                            ? `${
                                BANK_LIST_OBJECT[
                                  beneficiary.external_bank_account.ispb_code
                                ]
                              } (${
                                beneficiary.external_bank_account.ispb_code
                              })`
                            : beneficiary.external_bank_account.ispb_code
                          : '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        Agência
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.external_bank_account?.bank_branch || '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        Conta
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.external_bank_account?.bank_account
                          ? `${beneficiary.external_bank_account.bank_account}${
                              beneficiary.external_bank_account
                                ?.bank_account_digit
                                ? `-${beneficiary.external_bank_account.bank_account_digit}`
                                : ''
                            }`
                          : '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                  <>
                    <ReviewSimulationPreviewItem>
                      <TextM
                        weight="Semibold"
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        Tipo
                      </TextM>
                      <TextM
                        style={{ color: theme.palette.brand.secondary.base }}
                      >
                        {beneficiary.external_bank_account?.bank_account_type
                          ? ACCOUNT_TYPE_OBJECT[
                              beneficiary.external_bank_account
                                .bank_account_type
                            ] ?? '-'
                          : '-'}
                      </TextM>
                    </ReviewSimulationPreviewItem>
                    <ReviewStepSimulationDivider theme={theme} />
                  </>
                </ReviewSimulationPreviewItems>
              </ReviewSimulationBox>
            </ReviewStepBox>
          ))}
        {product?.type_product_emission ===
          TypeProductEmission.COMMERCIAL_PAPER && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              Custodiante:
            </Heading4>
            <ReviewSimulationBox>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      CNPJ
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.taxpayer_id ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Data de Constituição
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.foundation_date
                        ? new Date(
                            custodian?.foundation_date,
                          ).toLocaleDateString('pt-br')
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Telefone
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.phone?.number
                        ? formatPhone(custodian.phone.number)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Rua / Avenida
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.street_name
                        ? capitalize(custodian.address.street_name)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Bairro
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.district
                        ? capitalize(custodian.address.district)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Cidade
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.city?.value
                        ? capitalize(custodian.address.city.value)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Razão Social
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.legal_name ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      E-mail
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.email_address ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      CEP
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.postal_code ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Número
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.street_number ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Estado
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.state_code?.value ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Complemento
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {custodian?.address?.extra_info ?? '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
            </ReviewSimulationBox>
          </ReviewStepBox>
        )}
        {isNewSimulator && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              Valores da solicitação:
            </Heading4>
            <ReviewSimulationBox>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Quant. parcelas
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.num_periods &&
                      simulationV2?.num_periods > 0
                        ? simulationV2?.num_periods
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor da parcela
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.payment_amount
                        ? formatCurrency(simulationV2?.payment_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor solicitado
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.requested_amount
                        ? formatCurrency(simulationV2?.requested_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor liberado
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.disbursement_amount
                        ? formatCurrency(simulationV2?.disbursement_amount || 0)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor financiado
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.financed_amount
                        ? formatCurrency(simulationV2?.financed_amount || 0)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Juros
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.interest_rate
                        ? `${normalizeAddDigits(
                            simulationV2?.interest_rate * 100,
                            2,
                          )?.replace('.', ',')}% a.m.`
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      CET
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {typeof simulationV2?.monthly_effective_interest_rate ===
                      'number'
                        ? `${normalizeAddDigits(
                            simulationV2?.monthly_effective_interest_rate * 100,
                            2,
                          ).replace('.', ',')}% a.m.`
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      TC
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.tac_amount
                        ? formatCurrency(simulationV2?.tac_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      IOF
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.iof_amount
                        ? formatCurrency(simulationV2?.iof_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor do Seguro
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.insurance_amount
                        ? formatCurrency(simulationV2?.insurance_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Primeiro vencimento
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.first_payment_date
                        ? formatDateFromBackend(
                            simulationV2?.first_payment_date,
                          ).toLocaleDateString('pt-br')
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Último vencimento
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.last_payment_date
                        ? formatDateFromBackend(
                            simulationV2?.last_payment_date,
                          ).toLocaleDateString('pt-br')
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Saldo Total
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulationV2?.total_amount_owed
                        ? formatCurrency(simulationV2?.total_amount_owed)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
            </ReviewSimulationBox>
          </ReviewStepBox>
        )}
        {!isNewSimulator && (
          <ReviewStepBox marginTop="0px" marginBottom="16px">
            <Heading4
              style={{
                color: theme.palette.brand.secondary.base,
              }}
            >
              Valores da solicitação:
            </Heading4>
            <ReviewSimulationBox>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.primary.base }}
                    >
                      Quant. parcelas
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.numPeriods && simulation?.numPeriods > 0
                        ? simulation?.numPeriods
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor da parcela
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.paymentAmount
                        ? formatCurrency(simulation?.paymentAmount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor solicitado
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.requested_amount
                        ? formatCurrency(simulation?.requested_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor financiado
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.financed_amount
                        ? formatCurrency(simulation?.financed_amount || 0)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Juros
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.interestRate
                        ? `${normalizeAddDigits(
                            simulation?.interestRate * 100,
                            2,
                          )?.replace('.', ',')}% a.m.`
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      CET
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {typeof simulation?.monthlyEffectiveInterestRate ===
                      'number'
                        ? `${normalizeAddDigits(
                            simulation?.monthlyEffectiveInterestRate * 100,
                            2,
                          ).replace('.', ',')}% a.m.`
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
              <ReviewSimulationPreviewItems>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      TC
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.tacAmount
                        ? formatCurrency(simulation?.tacAmount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      IOF
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.iofAmount
                        ? formatCurrency(simulation?.iofAmount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Valor do Seguro
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.insurance_amount
                        ? formatCurrency(simulation?.insurance_amount)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Primeiro vencimento
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.firstPaymentDate
                        ? formatDateFromBackend(
                            simulation?.firstPaymentDate,
                          ).toLocaleDateString('pt-br')
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Último vencimento
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.lastPaymentDate
                        ? formatDateFromBackend(
                            simulation?.lastPaymentDate,
                          ).toLocaleDateString('pt-br')
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
                <>
                  <ReviewSimulationPreviewItem>
                    <TextM
                      weight="Semibold"
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      Saldo Total
                    </TextM>
                    <TextM
                      style={{ color: theme.palette.brand.secondary.base }}
                    >
                      {simulation?.totalAmountOwed
                        ? formatCurrency(simulation?.totalAmountOwed)
                        : '-'}
                    </TextM>
                  </ReviewSimulationPreviewItem>
                  <ReviewStepSimulationDivider theme={theme} />
                </>
              </ReviewSimulationPreviewItems>
            </ReviewSimulationBox>
          </ReviewStepBox>
        )}
        <ReviewStepBox marginTop="16px" marginBottom="16px" padding="30px 40px">
          <Heading4
            style={{
              color: theme.palette.brand.secondary.base,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip
              placement="bottom-start"
              title={
                <TextM style={{ color: theme.palette.common.white }}>
                  Este valor corresponde ao saldo necessário para liquidação da
                  CCB (Valor Financiado + Ágio).
                </TextM>
              }
            >
              <InfoOutlined
                style={{
                  marginRight: '0.6rem',
                  color: theme.palette.brand.gray[900],
                }}
              />
            </Tooltip>
            <strong>Saldo para liquidação: </strong>
            <span style={{ paddingLeft: '0.4rem' }}>{` ${formatCurrency(
              cessionValue,
              true,
            )}`}</span>
          </Heading4>
        </ReviewStepBox>
        {product?.type_product_emission ===
          TypeProductEmission.BILLET_PAYMENT &&
          successBillets.length > 0 && (
            <ReviewStepBox
              marginTop="16px"
              marginBottom="16px"
              padding="24px 8px"
            >
              <Accordion
                cleanStyle={true}
                isOpen={isBilletsAccordionOpen}
                title={<Heading4>Boletos</Heading4>}
                onClick={() =>
                  setIsBilletsAccordionOpen(!isBilletsAccordionOpen)
                }
              >
                <Table
                  tableHeadCell={[
                    '',
                    'Nº do documento',
                    'Nome do sacado',
                    'CPF / CNPJ',
                    'Vencimento',
                    'Valor',
                  ].map((header, index) => ({
                    id: String(index),
                    content: (
                      <TableHeadCellContainer>
                        <TextS
                          weight="Bold"
                          style={{ marginLeft: index >= 2 ? '12px' : '0px' }}
                        >
                          {header}
                        </TextS>
                      </TableHeadCellContainer>
                    ),
                  }))}
                  tableBodyContent={successBillets.map((billet, inx) => {
                    return {
                      id: '1',
                      cells: [
                        {
                          id: '1',
                          content: (
                            <TableBodyCellContainer>
                              <TextS weight="Bold">{inx + 1}</TextS>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '2',
                          content: (
                            <TableBodyCellContainer>
                              <TextS
                                style={{
                                  overflow: 'hidden',
                                  display: 'inline-block',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '150px',
                                  marginTop: '5px',
                                  textOverflow: 'ellipsis',
                                }}
                                weight="Regular"
                              >
                                {getBilletNumber(billet.request)}
                              </TextS>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '3',
                          content: (
                            <TableBodyCellContainer
                              style={{
                                overflow: 'hidden',
                                whiteSpace: 'break-spaces',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <TextS
                                style={{ width: 'fit-content' }}
                                weight="Regular"
                              >
                                {billet.result.payer}
                              </TextS>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '4',
                          content: (
                            <TableBodyCellContainer
                              style={{
                                overflow: 'hidden',
                                whiteSpace: 'break-spaces',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <TextS
                                style={{ width: 'fit-content' }}
                                weight="Regular"
                              >
                                {billet.result.document_payer}
                              </TextS>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '5',
                          content: (
                            <TableBodyCellContainer
                              style={{
                                overflow: 'hidden',
                                whiteSpace: 'break-spaces',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <TextS
                                style={{ width: 'fit-content' }}
                                weight="Regular"
                              >
                                {billet.result.due_date
                                  ? formatDateFromBackend(
                                      billet.result.due_date,
                                    ).toLocaleDateString('pt-BR')
                                  : '-'}
                              </TextS>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '6',
                          content: (
                            <TableBodyCellContainer
                              style={{
                                overflow: 'hidden',
                                whiteSpace: 'break-spaces',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <TextS
                                style={{ width: 'fit-content' }}
                                weight="Regular"
                              >
                                {formatCurrency(billet.result.amount)}
                              </TextS>
                            </TableBodyCellContainer>
                          ),
                        },
                      ],
                    };
                  })}
                  tableFooterCell={[
                    {
                      id: '1',
                      content: null,
                    },
                    {
                      id: '2',
                      content: null,
                    },
                    {
                      id: '3',
                      content: null,
                    },
                    {
                      id: '4',
                      content: null,
                    },
                    {
                      id: '5',
                      content: null,
                    },
                    {
                      id: '6',
                      content: (
                        <TableBodyCellContainer>
                          <div style={{ display: 'block' }}>
                            <div>
                              <TextM>{`Valor Total:`}</TextM>
                            </div>
                            <div>
                              <TextM weight="Bold">
                                {formatCurrency(
                                  billetValidationResponse?.total_value ?? 0,
                                )}
                              </TextM>
                            </div>
                          </div>
                        </TableBodyCellContainer>
                      ),
                    },
                    {
                      id: '7',
                      content: null,
                    },
                  ]}
                />
              </Accordion>
            </ReviewStepBox>
          )}
        {product?.type_product_emission === TypeProductEmission.QRCODE_PIX && (
          <ReviewStepBox
            marginTop="16px"
            marginBottom="16px"
            padding="24px 8px"
          >
            <Accordion
              cleanStyle={true}
              isOpen={isQRCodeAccordionOpen}
              title={<Heading4>Pix copia e cola</Heading4>}
              onClick={() => setIsQRCodeAccordionOpen(!isQRCodeAccordionOpen)}
            >
              <QRCodeContainer>
                <QRCodeDetails
                  simulator={true}
                  review={true}
                  qrcodeEmvResponse={qrcodeEmvValidationResponse!}
                />
              </QRCodeContainer>
            </Accordion>
          </ReviewStepBox>
        )}
        <ReviewStepBox marginTop="16px" marginBottom="16px" padding="24px 8px">
          <Accordion
            cleanStyle={true}
            isOpen={isAccordionOpen}
            title={<Heading4>Parcelas</Heading4>}
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          >
            {!isNewSimulator &&
            product?.multiple_disbursements_dates &&
            simulation ? (
              <DisbursementDatesSelector>
                <CustomSelect
                  labelValue="Selecione a data de desembolso"
                  options={new Array(
                    differenceInDays(
                      startOfDay(new Date(simulation.disbursement_date!)),
                      startOfDay(new Date()),
                    ) + 2,
                  )
                    .fill(0)
                    .map((_, index) => {
                      const date = new Date();
                      date.setDate(date.getDate() + index);
                      return {
                        label: format(date, 'dd/MM/yyyy'),
                        value: startOfDay(date).toISOString(),
                      };
                    })}
                  handleInputChange={(evt) => {
                    const { value } = evt.currentTarget.value;
                    simulateWithDifferentDisbursementDate(value);
                  }}
                  isClearable={false}
                  value={
                    finalSimulation
                      ? {
                          label: format(
                            formatDateFromBackend(
                              finalSimulation.disbursement_date!,
                            ),
                            'dd/MM/yyyy',
                          ),
                          value: startOfDay(
                            formatDateFromBackend(
                              finalSimulation.disbursement_date!,
                            ),
                          ).toISOString(),
                        }
                      : undefined
                  }
                  name="disbursementScheduleDate"
                  placeholder="Selecionar"
                />
              </DisbursementDatesSelector>
            ) : (
              <CreditSimulatorSchedule
                noHeader
                product={product}
                simulationRequest={simulationV2Request}
                simulationResponse={simulationV2}
                simulateWithDifferentDisbursementDate={
                  simulateWithDifferentDisbursementDate
                }
              />
            )}
            <div style={{ marginTop: '15px' }}>
              {finalSimulation?.schedule &&
              Array.isArray(finalSimulation?.schedule) &&
              finalSimulation?.schedule.length > 0 ? (
                <>
                  <Table
                    maxBodyHeight="55vh"
                    tableHeadSticky={true}
                    tableHeadCell={tableHeadContent}
                    isLoading={simulationLoading}
                    tableBodyContent={finalSimulation.schedule.map(
                      (simulationData, inx) => {
                        const iof = baseIofPerInstallment + simulationData.iof;
                        return {
                          id: String(inx),
                          cells: [
                            {
                              id: '1',
                              content: (
                                <TableBodyCellContainer>
                                  <TextM
                                    style={{ margin: '0 auto' }}
                                    weight="Bold"
                                  >
                                    {inx + 1}
                                  </TextM>
                                </TableBodyCellContainer>
                              ),
                            },
                            {
                              id: '2',
                              content: (
                                <TableBodyCellContainer>
                                  <TextM>
                                    {formatCurrency(simulationData.payment)}
                                  </TextM>
                                </TableBodyCellContainer>
                              ),
                            },
                            {
                              id: '3',
                              content: (
                                <TableBodyCellContainer>
                                  <TextM>
                                    {formatCurrency(simulationData.principal)}
                                  </TextM>
                                </TableBodyCellContainer>
                              ),
                            },
                            {
                              id: '4',
                              content: (
                                <TableBodyCellContainer>
                                  <TextM>
                                    {formatCurrency(simulationData.interest)}
                                  </TextM>
                                </TableBodyCellContainer>
                              ),
                            },
                            {
                              id: '5',
                              content: (
                                <TableBodyCellContainer>
                                  <TextM>{formatCurrency(iof)}</TextM>
                                </TableBodyCellContainer>
                              ),
                            },
                            {
                              id: '6',
                              content: (
                                <TableBodyCellContainer>
                                  <TextM>
                                    {formatDateFromBackend(
                                      simulationData.payment_date,
                                    ).toLocaleDateString('pt-BR')}
                                  </TextM>
                                </TableBodyCellContainer>
                              ),
                            },
                          ],
                        };
                      },
                    )}
                    tableFooterCell={[
                      {
                        id: '1',
                        content: null,
                      },
                      {
                        id: '2',
                        content: (
                          <TableBodyCellContainer>
                            <div style={{ display: 'block' }}>
                              <div>
                                <TextM>Total</TextM>
                              </div>
                              <div>
                                <TextM weight="Bold">
                                  {formatCurrency(
                                    finalSimulation.schedule
                                      .map((schedule) => schedule.payment)
                                      .reduce((a, b) => a + b, 0),
                                  )}
                                </TextM>
                              </div>
                            </div>
                          </TableBodyCellContainer>
                        ),
                      },
                      {
                        id: '3',
                        content: (
                          <TableBodyCellContainer>
                            <div style={{ display: 'block' }}>
                              <div>
                                <TextM>Total</TextM>
                              </div>
                              <div>
                                <TextM weight="Bold">
                                  {formatCurrency(
                                    finalSimulation.schedule
                                      .map((schedule) => schedule.principal)
                                      .reduce((a, b) => a + b, 0),
                                  )}
                                </TextM>
                              </div>
                            </div>
                          </TableBodyCellContainer>
                        ),
                      },
                      {
                        id: '4',
                        content: (
                          <TableBodyCellContainer>
                            <div style={{ display: 'block' }}>
                              <div>
                                <TextM>Total</TextM>
                              </div>
                              <div>
                                <TextM weight="Bold">
                                  {formatCurrency(
                                    finalSimulation.schedule
                                      .map((schedule) => schedule.interest)
                                      .reduce((a, b) => a + b, 0),
                                  )}
                                </TextM>
                              </div>
                            </div>
                          </TableBodyCellContainer>
                        ),
                      },
                      {
                        id: '5',
                        content: (
                          <TableBodyCellContainer>
                            <div style={{ display: 'block' }}>
                              <div>
                                <TextM>Total</TextM>
                              </div>
                              <div>
                                <TextM weight="Bold">
                                  {formatCurrency(
                                    finalSimulation.schedule
                                      .map(
                                        (schedule) =>
                                          baseIofPerInstallment + schedule.iof,
                                      )
                                      .reduce((a, b) => a + b, 0),
                                  )}
                                </TextM>
                              </div>
                            </div>
                          </TableBodyCellContainer>
                        ),
                      },
                      {
                        id: '6',
                        content: null,
                      },
                    ]}
                  />
                  <ButtonPrimary
                    style={{
                      backgroundColor: 'transparent',
                      width: 'fit-content',
                      margin: '15px auto 0px',
                    }}
                    typeVariant="outline"
                    btnIcon={
                      <CopyIcon
                        color={theme.palette.brand.primary.base as string}
                      />
                    }
                    iconPosition="left"
                    onClick={handleCopySchedule}
                  >
                    <TextM
                      weight="Semibold"
                      style={{
                        marginLeft: '11px',
                        color: theme.palette.brand.primary.base,
                      }}
                    >
                      Copiar resultado
                    </TextM>
                  </ButtonPrimary>
                </>
              ) : !isNewSimulator ? (
                <TextM>
                  Nenhuma parcela disponível. Conclua primeiro a simulação no
                  step de valores da solicitação.
                </TextM>
              ) : undefined}
            </div>
          </Accordion>
        </ReviewStepBox>
        <ReviewStepBox marginTop="16px" marginBottom="16px">
          <Heading4
            style={{
              color: theme.palette.brand.secondary.base,
            }}
          >
            Assinantes
          </Heading4>
          <ReviewStepBorrowerBox>
            <Table
              tableHeadCell={tableHeader.map((header, index) => ({
                id: String(index),
                content: (
                  <TableHeadCellContainer>
                    <TextM weight="Bold">{header}</TextM>
                  </TableHeadCellContainer>
                ),
              }))}
              tableBodyContent={[
                {
                  id: '1',
                  cells: [
                    {
                      id: '1',
                      content: (
                        <TableBodyCellContainer>
                          <TextM weight="Bold">Tomador</TextM>
                        </TableBodyCellContainer>
                      ),
                    },
                    {
                      id: '2',
                      content: (
                        <TableBodyCellContainer>
                          <TextM weight="Regular">
                            {isPerson()
                              ? borrowerData?.full_name
                              : borrowerData?.legal_name}
                          </TextM>
                        </TableBodyCellContainer>
                      ),
                    },
                    {
                      id: '3',
                      content: (
                        <TableBodyCellContainer>
                          <TextM weight="Regular">
                            {isPerson()
                              ? formatCPF(borrowerData?.taxpayer_id || '')
                              : formatCNPJ(borrowerData?.taxpayer_id || '')}
                          </TextM>
                        </TableBodyCellContainer>
                      ),
                    },
                    {
                      id: '4',
                      content: (
                        <TableBodyCellContainer>
                          <TextM weight="Regular">
                            {formatPhone(
                              (borrowerData?.phone?.area_code || '') +
                                (borrowerData?.phone?.number || ''),
                            )}
                          </TextM>
                        </TableBodyCellContainer>
                      ),
                    },
                    {
                      id: '5',
                      content: (
                        <TableBodyCellContainer>
                          <TextM weight="Regular">
                            {borrowerData?.email_address}
                          </TextM>
                        </TableBodyCellContainer>
                      ),
                    },
                  ],
                },
              ].concat(
                subscribers?.map(
                  ({ subscriber, subscriberType, subscriberPersonType }) => {
                    return {
                      id: '1',
                      cells: [
                        {
                          id: '1',
                          content: (
                            <TableBodyCellContainer>
                              <TextM weight="Bold">
                                {SubscriberName[subscriberType]}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '2',
                          content: (
                            <TableBodyCellContainer>
                              <TextM weight="Regular">
                                {subscriber?.full_name}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '3',
                          content: (
                            <TableBodyCellContainer>
                              <TextM weight="Regular">
                                {subscriberPersonType === 'PERSON'
                                  ? formatCPF(subscriber?.taxpayer_id || '')
                                  : formatCNPJ(subscriber?.taxpayer_id || '')}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '4',
                          content: (
                            <TableBodyCellContainer>
                              <TextM weight="Regular">
                                {formatPhone(
                                  (subscriber?.phone?.area_code || '') +
                                    (subscriber?.phone?.number || ''),
                                )}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                        {
                          id: '5',
                          content: (
                            <TableBodyCellContainer>
                              <TextM weight="Regular">
                                {subscriber?.email_address}
                              </TextM>
                            </TableBodyCellContainer>
                          ),
                        },
                      ],
                    };
                  },
                ) || [],
              )}
            />
          </ReviewStepBorrowerBox>
        </ReviewStepBox>
        {isPaymentMethodVisible && (
          <ReviewStepBox marginTop="16px" marginBottom="16px">
            <SignTypeContainer>
              <Heading4 style={{ color: theme.palette.brand.secondary.base }}>
                Método de pagamento:{' '}
              </Heading4>
              <div className="bullet-area">
                <div className="bullet-item">
                  <CustomRadioInput
                    isChecked={paymentMethod === 'PIX'}
                    handleClick={() => onChangePaymentMethod(PaymentMethod.PIX)}
                  />
                  <TextL>Pix</TextL>
                </div>
                <div className="bullet-item">
                  <CustomRadioInput
                    isChecked={paymentMethod === 'TED'}
                    handleClick={() => onChangePaymentMethod(PaymentMethod.TED)}
                  />
                  <TextL>TED</TextL>
                </div>
              </div>
            </SignTypeContainer>
          </ReviewStepBox>
        )}
        <ReviewStepBox marginTop="16px" marginBottom="16px">
          <SignTypeContainer>
            <Heading4 style={{ color: theme.palette.brand.secondary.base }}>
              Provedor de assinatura:{' '}
            </Heading4>
            <div className="bullet-area">
              {availableSignatureProviders.map((provider) => (
                <div key={provider.value} className="bullet-item">
                  <CustomRadioInput
                    isChecked={selectedSignatureProvider === provider.value}
                    handleClick={() =>
                      setSelectedSignatureProvider(provider.value)
                    }
                  />
                  <TextL>{provider.label}</TextL>
                </div>
              ))}
            </div>
          </SignTypeContainer>
          {selectedSignatureProvider === SignatureProvider.CLICKSIGN && (
            <SignTypeContainer style={{ marginTop: '2rem' }}>
              <TextL
                style={{
                  color: theme.palette.brand.secondary.base,
                }}
              >
                Método de assinatura:{' '}
              </TextL>
              <div>
                <CustomSelect
                  styles={{
                    width: '350px',
                  }}
                  value={signatureOptions.find(
                    (opt) => opt.value === signatureMethod,
                  )}
                  options={signatureOptions}
                  handleInputChange={(evt) => {
                    updateSignatureMethod(evt.currentTarget.value?.value);
                  }}
                  name="signatureMethod"
                  placeholder="Selecionar"
                  isClearable={false}
                />
              </div>
            </SignTypeContainer>
          )}
          {/* Quando Unico for selecionada */}
          {selectedSignatureProvider === SignatureProvider.UNICO && (
            <SignTypeContainer style={{ marginTop: '2rem' }}>
              <TextL
                style={{
                  color: theme.palette.brand.secondary.base,
                }}
              >
                Método de assinatura:{' '}
              </TextL>
              <div>
                <CustomSelect
                  styles={{
                    width: '350px',
                  }}
                  value={signatureUnicoOptions.find(
                    (opt) => opt.value === signatureMethodUnico,
                  )}
                  options={signatureUnicoOptions}
                  handleInputChange={(evt) => {
                    setSignatureMethodUnico(evt.currentTarget.value?.value);
                  }}
                  name="signatureMethod"
                  placeholder="Selecionar"
                  isClearable={false}
                />
              </div>
            </SignTypeContainer>
          )}
        </ReviewStepBox>
        <ReviewStepBox marginTop="16px" marginBottom="32px">
          <SignTypeContainer>
            <Heading4 style={{ color: theme.palette.brand.secondary.base }}>
              Receber assinatura por:{' '}
            </Heading4>
            <div className="bullet-area">
              <div className="bullet-item">
                <CustomRadioInput
                  isChecked={selectedSignatureType === 'WHATSAPP'}
                  handleClick={() => handleSelectSignatureType('WHATSAPP')}
                />
                <TextL>WhatsApp</TextL>
              </div>
              <div className="bullet-item">
                <CustomRadioInput
                  isChecked={
                    selectedSignatureProvider === SignatureProvider.UNICO
                      ? selectedSignatureType === 'SMS'
                      : selectedSignatureType === 'EMAIL'
                  }
                  handleClick={() =>
                    handleSelectSignatureType(
                      selectedSignatureProvider === SignatureProvider.UNICO
                        ? 'SMS'
                        : 'EMAIL',
                    )
                  }
                />
                <TextL>
                  {selectedSignatureProvider === SignatureProvider.UNICO
                    ? 'SMS'
                    : 'E-mail'}
                </TextL>
              </div>
              <div className="bullet-item">
                <CustomRadioInput
                  isChecked={selectedSignatureType === 'LINK'}
                  handleClick={() => handleSelectSignatureType('LINK')}
                />
                <TextL>Link</TextL>
              </div>
            </div>
          </SignTypeContainer>
        </ReviewStepBox>
        <StepContentButtonsArea>
          <ButtonDefault
            margin={true}
            onClick={() =>
              shouldSkipVariablesStep ? onBackTwoSteps() : onBack()
            }
          >
            <ArrowBack />
          </ButtonDefault>
          <ButtonPrimary
            disabled={
              (isNewSimulator ? !simulationV2 : !simulation) ||
              !selectedSignatureProvider ||
              (product?.beneficiary_type ===
                EBeneficiaryType.EXTERNAL_REGISTERED_ACCOUNT &&
                !beneficiaryAccount)
            }
            onClick={() => toggleConfirmModalOpen(true)}
          >
            Concluir
          </ButtonPrimary>
        </StepContentButtonsArea>
      </ReviewStepContent>
      <ConfirmModal
        isOpen={isConfirmModalOpen !== false}
        icon={
          <HandCoinBiggerIcon
            color={theme.palette.brand.primary.base as string}
          />
        }
        title={
          consigned
            ? 'Concluir solicitação?'
            : 'Concluir solicitação e ir para página de assinaturas?'
        }
        color="primary"
        btnConfirmText="Concluir"
        isLoading={loadings.isApplicationLoading}
        handleConfirm={onCreateApplication}
        handleClose={() => toggleConfirmModalOpen(false)}
        handleCancel={() => toggleConfirmModalOpen(false)}
      >
        {consigned
          ? 'Após concluir a solicitação, a operação será encaminhada para a próxima etapa.'
          : 'Após a assinatura das partes, a solicitação será submetida para aprovação (se necessário).'}
      </ConfirmModal>
      <ConfirmModal
        isColumnButtons={true}
        isOpen={tedErrorType !== undefined}
        icon={<AttentionCircularIcon />}
        title={
          tedErrorType === TedErrorType.BORROWER
            ? 'Tomador não possui conta bancária'
            : 'Fora do Horário'
        }
        btnConfirmText="Entendi"
        handleConfirm={() => setTedErrorType(undefined)}
        handleClose={() => setTedErrorType(undefined)}
      >
        <TedModalContent>
          {tedErrorType === TedErrorType.BORROWER ? (
            <>
              <TextM style={{ lineHeight: '1.3rem' }}>
                O tomador{' '}
                <strong>
                  {borrowerType === 'PERSON'
                    ? borrowerData?.full_name
                    : borrowerData?.legal_name}
                </strong>{' '}
                não possui conta bancária cadastrada para o método de pagamento
                TED.
              </TextM>
              <a
                href={
                  borrowerType === 'PERSON'
                    ? `#/records/natural/${borrowerData?.id}/details`
                    : `#/records/legal/${borrowerData?.id}/details`
                }
                target="_blank"
                style={{
                  textDecoration: 'underline',
                  marginTop: '20px',
                }}
                className="link"
                rel="noreferrer"
              >
                Ir para o cadastro
              </a>
            </>
          ) : (
            <TextM style={{ lineHeight: '1.3rem' }}>
              A <strong>data de liberação do recurso</strong> será hoje e o
              método de pagamento TED só pode ser realizado até às 16h.
            </TextM>
          )}
        </TedModalContent>
      </ConfirmModal>
    </BoxSection>
  );
};

export default ReviewStep;
